/**
 * billingPricing.js
 *
 * Helpers de precio y contratos por sector. Espejados del modelo del lambda
 * a_stripeWebhook (WORKER_TIERED_MODE + SALON_SECTORS + RESTAURACION_SECTORS).
 *
 * Tabla de precios:
 *   - Sectores "salón" (peluqueria/barberia/estetica/spa): 40 €/mes base (2 trab)
 *   - Lavadero: 40 €/mes base (2 trab) + IVA, 1.000 plantillas incluidas
 *   - Sectores "general" (fisioterapia, salud, consultoria, despacho_abogados,
 *     autoescuela): 40 €/mes base (2 trab)
 *   - Sectores "restauración" (bares_deportes, food_orders): 50 €/mes plano,
 *     sin línea de trabajadores en la factura
 *   - Tramos por UNIDADES de media jornada (jornada completa = 2, media = 1):
 *     ver surchargeForUnits en ./workerUnits.js — <=4 u = base, 5 u = base
 *     +2,50€, 6-8 u = base +5€, 9 u = base +7,50€, 10-12 u = base +10€,
 *     >12 u = contactar ventas.
 *   - Yearly = monthly × 10 (descuento equivalente a 2 meses)
 *
 * URL del PDF del contrato:
 *   https://conextcrm-uploads.s3.eu-north-1.amazonaws.com/legal/<slug>/contrato-suscripcion-v<TOS_VERSION>.pdf
 */

import {
  surchargeForUnits,
  weeklyHours,
  classifyWorker,
  FULL_TIME_UNITS,
  HALF_TIME_UNITS,
  MAX_BILLABLE_UNITS,
} from "./workerUnits";

export const TOS_VERSION = "1.0";
// Deprecada: el modelo de cobro pasó de "cabezas" a unidades de media jornada
// (ver ./workerUnits.js). Se mantiene exportada mientras haya consumidores
// que sigan contando trabajadores en vez de unidades.
export const MAX_BILLABLE_WORKERS = 6;
export const SALES_CONTACT_EMAIL = "info@conextcrm.com";
const CONTRACTS_BASE_URL =
  "https://conextcrm-uploads.s3.eu-north-1.amazonaws.com/legal";

const SALON = new Set(["peluqueria", "barberia", "estetica", "spa", "canina", "tatuajes"]);
const LAVADERO = new Set(["lavadero"]);
const GENERAL = new Set([
  "fisioterapia",
  "osteopatia",
  "psicologia",
  "salud",
  "consultoria",
  "despacho_abogados",
  "autoescuela",
]);
// Sectores de CUOTA PLANA (el nº de trabajadores no afecta al precio): en comidas no hay
// una agenda por trabajador que justifique el recargo, a diferencia de citas.
// OJO: aquí llega el SECTOR de c_empresas, no el app_type — "food_orders" es app_type y
// nunca llega; los sectores reales son comida_para_llevar / pizzeria / distribucion /
// restaurantes (los de SECTOR_TO_APP_TYPE en v_create_client). Espejo de
// RESTAURACION_SECTORS en a_stripeWebhook: si se toca uno, se tocan los dos.
const RESTAURACION = new Set([
  "restauracion",
  "restaurantes",
  "comida_para_llevar",
  "pizzeria",
  "distribucion",
  "bares_deportes",
  "bares_tematicos",
  "food_orders",
]);

// Devuelve el slug canónico del sector (mismo que usan los PDFs en S3 y el lambda).
export function normalizeSector(sector) {
  const s = String(sector || "").toLowerCase().trim();
  const map = {
    peluquerias: "peluqueria",
    peluqueria_unisex: "peluqueria",
    "peluquería": "peluqueria",
    "peluquerías": "peluqueria",
    "barbería": "barberia",
    "estética": "estetica",
    "consultoría": "consultoria",
    "despacho abogados": "despacho_abogados",
    "restauración": "restauracion",
    bar_sports: "bares_deportes",
  };
  const normalized = map[s] || s;
  // Restauración: el slug del PDF es "restauracion" para todos
  if (RESTAURACION.has(normalized)) return "restauracion";
  return normalized;
}

export function getSectorDisplayName(sector) {
  const slug = normalizeSector(sector);
  const names = {
    peluqueria: "Peluquería",
    barberia: "Barbería",
    estetica: "Estética",
    canina: "Peluquería canina",
    tatuajes: "Tatuajes",
    spa: "Spa",
    fisioterapia: "Fisioterapia",
    osteopatia: "Osteopatía",
    psicologia: "Psicología",
    salud: "Salud",
    consultoria: "Consultoría",
    despacho_abogados: "Despacho de Abogados",
    lavadero: "Lavadero",
    autoescuela: "Autoescuela",
    restauracion: "Restauración",
  };
  return names[slug] || slug;
}

// Sectores sin PDF propio en S3 que firman el contrato de otro (mismas
// condiciones y mismo precio). Canina no tiene carpeta legal/canina/: usa el de
// peluquería, que es su mismo plan de salón. Sin esto el enlace da 404.
// Psicología y osteopatía tampoco tienen carpeta propia en legal/: firman el
// de salud, que es su mismo plan y las mismas condiciones.
const CONTRACT_SLUG_ALIAS = { canina: "peluqueria", tatuajes: "peluqueria", psicologia: "salud", osteopatia: "salud" };

export function getSectorContractUrl(sector) {
  const slug = normalizeSector(sector);
  const contractSlug = CONTRACT_SLUG_ALIAS[slug] || slug;
  return `${CONTRACTS_BASE_URL}/${contractSlug}/contrato-suscripcion-v${TOS_VERSION}.pdf`;
}

export function isWorkerBillableForSector(sector) {
  const slug = normalizeSector(sector);
  return !RESTAURACION.has(slug);
}

// Devuelve metadata del plan para el sector: precio base, modo, etc.
export function getSectorPricing(sector) {
  const slug = normalizeSector(sector);
  if (RESTAURACION.has(slug)) {
    return { mode: "flat", basePrice: 40, hasTiers: false, sectorSlug: slug };
  }
  if (SALON.has(slug) || LAVADERO.has(slug)) {
    return { mode: "tiered", basePrice: 40, hasTiers: true, sectorSlug: slug };
  }
  // Default = general (40 €) con tiers
  return { mode: "tiered", basePrice: 40, hasTiers: true, sectorSlug: slug };
}

// Calcula el recargo mensual según el tramo de unidades de media jornada
// (jornada completa = 2, media = 1). Delega en surchargeForUnits de
// ./workerUnits.js — una sola tabla de tramos, no dos. Si units >
// MAX_BILLABLE_UNITS devuelve null → contactar ventas.
export function getWorkerSurcharge(units) {
  return surchargeForUnits(units);
}

// Precio mensual total (base + recargo) en euros. Devuelve null si fuera de tramos.
export function getMonthlyPrice(sector, units) {
  const pricing = getSectorPricing(sector);
  if (!pricing.hasTiers) return pricing.basePrice;
  const sur = getWorkerSurcharge(units);
  if (sur === null) return null;
  return pricing.basePrice + sur;
}

// Precio anual (× 10 meses, descuento de 2 meses gratis). Null si fuera de tramos.
export function getYearlyPrice(sector, units) {
  const monthly = getMonthlyPrice(sector, units);
  return monthly === null ? null : monthly * 10;
}

// Describe la plantilla en NÚMEROS, nunca en texto: este fichero es un helper
// puro y no tiene acceso a t(). Cada componente lo pinta con su i18n.
// units = full*2 + half — la misma cuenta que computeUnits en el backend.
export function describeWorkforce(fullCount, halfCount) {
  const full = Math.max(0, Math.floor(Number(fullCount) || 0));
  const half = Math.max(0, Math.floor(Number(halfCount) || 0));
  return {
    fullCount: full,
    halfCount: half,
    units: full * FULL_TIME_UNITS + half * HALF_TIME_UNITS,
  };
}

// Traduce la respuesta de GET /stripe/billing-details a jornadas.
//
// Con WORKER_UNITS_MODE apagado el backend NO lee los horarios: manda
// current_units/full_time_workers/half_time_workers a null y solo current_workers
// es real (a_stripeWebhook/companyUnits.js:108). Ahí cada cabeza cuenta como
// jornada completa = 2 unidades: por el invariante surchargeForUnits(2n) === el
// tramo actual de n, eso da exactamente el precio que se factura hoy.
export function workforceFromBillingDetails(billingDetails) {
  if (!billingDetails) return describeWorkforce(0, 0);
  if (billingDetails.worker_pricing_mode === "units") {
    return describeWorkforce(
      billingDetails.full_time_workers,
      billingDetails.half_time_workers,
    );
  }
  return describeWorkforce(billingDetails.current_workers, 0);
}

// Igual, pero desde horarios en crudo: el preview del checkout necesita las
// unidades ANTES de que exista suscripción, y recién dado de alta un trabajador
// billing-details puede venir cacheado. schedulesByUserId llega tal cual de
// GET /peluqueria/workers-schedules → { schedules: { [user_id]: schedule } }.
export function describeWorkforceFromSchedules(workerIds, schedulesByUserId = {}) {
  const ids = Array.isArray(workerIds) ? workerIds : [];
  const full = ids.filter(
    (id) => classifyWorker(weeklyHours(schedulesByUserId?.[id])) === "full",
  ).length;
  return describeWorkforce(full, ids.length - full);
}


// Devuelve true si units excede el máximo facturable (>12) y hay que mostrar
// CTA de contactar ventas en lugar de permitir el checkout.
export function requiresSalesContact(sector, units) {
  if (!isWorkerBillableForSector(sector)) return false;
  return Number(units) > MAX_BILLABLE_UNITS;
}

// Sectores de pedidos: mismo layout A/B, copy propio ("confirmaciones de pedido")
// y, en número propio, el bloque pay-per-use estándar (0,06€/0,026€): utilidad de Meta
// (~0,016€) + 0,01€ de margen. El excedente de automáticas con bote es 0,018€ desde el 1/10/2026 (antes 0,03€).
const PEDIDOS_SECTORS = new Set([
  "comida_para_llevar",
  "pizzeria",
  "distribucion",
]);

const AB_PRICING_SECTORS = new Set([
  "lavadero",
  "peluqueria",
  "barberia",
  "estetica",
  "spa",
  ...PEDIDOS_SECTORS,
  // Autoescuela: general, opera por su WABA shared de Conext. Copy propio y, en
  // número propio, el bloque pay-per-use estándar (como pedidos, no como citas).
  "autoescuela",
]);

export function isSalonSector(sector) {
  return SALON.has(normalizeSector(sector));
}

export function isPedidosSector(sector) {
  return PEDIDOS_SECTORS.has(normalizeSector(sector));
}

export function isAutoescuelaSector(sector) {
  return normalizeSector(sector) === "autoescuela";
}

export function isAbPricingSector(sector) {
  return AB_PRICING_SECTORS.has(normalizeSector(sector));
}
